import styled from '@emotion/styled';
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    ButtonGroup,
    Form,
    FormInputTextarea,
    HeadingText30,
    HeadingText60,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { color, font } from 'src/shared/styles/variables';
import { useAddMemberCarePreferenceMutation } from 'src/modules/user/documents/addMemberCarePreferenceMutation.generated';

export const supportWithSpecialtiesPills = [
    'ADHD',
    'Anxiety',
    'Autism',
    'Depression',
    'Eating Disorder',
    'Grief',
    'PostPartum',
    'OCD',
    'Substance Use',
    'Trauma',
    'Health Psychology',
    'Couples Counseling',
    'Family Therapy',
    'Other',
];

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: 1000,
});

const TilesWrapper = styled.ul({
    margin: 0,
    padding: 0,
    maxWidth: 670,
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    '&::after': {
        content: '""',
        marginTop: 12,
        width: '32%',
        padding: '15px 0',
    },
});

interface TileProps {
    selected: boolean;
}

const Tile = styled.li<TileProps>(props => ({
    marginTop: 12,
    width: '32%',
    textAlign: 'center',
    padding: pxToRem(15, 0),
    opacity: props.selected ? 1 : 0.6,
    borderRadius: 6,
    backgroundColor: props.selected ? color.royalBlue : color.catskillWhite,
    color: props.selected ? color.white : color.blueBayoux,
    transition: '.3s all',

    ':hover': {
        cursor: 'pointer',
        opacity: 1,
    },
    ...font.heavy(font.size.base),
}));

const StyledTextArea = styled(FormInputTextarea)({
    justifySelf: 'center',
    width: pxToRem(670),
});

interface Props {
    onPreviousPress: () => void;
    onContinuePress: () => void;
}

type FormData = {
    otherSpecialtiesFreeform?: string;
};

export const TherapySupportWith: React.FC<Props> = ({ onPreviousPress, onContinuePress }) => {
    const { t } = useTranslation();

    const [supportWithSpecialties, setSupportWithSpecialties] = useState<string[]>([]);

    const shouldRenderTextArea = () => supportWithSpecialties.includes('Other');

    const schema = Yup.object<FormData>().shape({
        otherSpecialtiesFreeform: Yup.string().when({
            is: shouldRenderTextArea,
            then: schema => schema.required(),
            otherwise: schema => schema,
        }),
    });

    const formDefinition = useForm<FormData>({
        resolver: yupResolver(schema),
    });

    const { isSubmitting, isValid } = formDefinition.formState;

    const dataValid = supportWithSpecialties.includes('Other')
        ? isValid
        : supportWithSpecialties.length > 0;

    const [addMemberCarePreferenceMutation] = useAddMemberCarePreferenceMutation({
        variables: {
            specialtiesSelected: supportWithSpecialties,
            otherSpecialtiesFreeform: formDefinition.getValues('otherSpecialtiesFreeform') || '',
        },
    });

    const handleTilePress = useCallback((text: string) => {
        setSupportWithSpecialties(specialties => {
            const newSpecialties = specialties.includes(text)
                ? specialties.filter(value => value !== text)
                : [...specialties, text];

            return newSpecialties;
        });
    }, []);

    const handleSubmit = async () => {
        await addMemberCarePreferenceMutation();
        onContinuePress();
    };

    return (
        <Wrapper>
            <HeadingText60>{t('providers:therapy_support.title')}</HeadingText60>
            <HeadingText30 fontColor="gray40">
                {t('providers:therapy_support.description')}
            </HeadingText30>

            <TilesWrapper>
                {supportWithSpecialtiesPills.map(specialty => {
                    const specialtyKey = specialty.replace(' ', '_').toLowerCase();
                    return (
                        <Tile
                            key={specialty}
                            selected={supportWithSpecialties.includes(specialty)}
                            onClick={() => handleTilePress(specialty)}
                        >
                            {t(`providers:therapy_support.specialty.${specialtyKey}`)}
                        </Tile>
                    );
                })}
            </TilesWrapper>

            <Form
                definition={formDefinition}
                onSubmit={handleSubmit}
                css={{ width: '50%', marginTop: pxToRem(40) }}
            >
                {shouldRenderTextArea() ? (
                    <StyledTextArea
                        name="otherSpecialtiesFreeform"
                        label={t('providers:therapy_support.help')}
                        rows={2}
                        required
                        trim
                    />
                ) : null}

                <ButtonGroup>
                    <Button onClick={onPreviousPress} variant="secondary" size="large">
                        {t('common.previous')}
                    </Button>
                    <Button
                        busy={isSubmitting}
                        disabled={!dataValid}
                        type="submit"
                        variant="primary"
                    >
                        {t('common.continue')}
                    </Button>
                </ButtonGroup>
            </Form>
        </Wrapper>
    );
};
